/**
 * Typed calls for the caller's own Mollie subscription — `GET
 * /subscriptions/{entitlement,products,history}` and the write path
 * (`start`/`cancel`/`switch-plan`/`withdraw`). Unlike `features/dashboards/api.ts`
 * these paths are already declared in `src/api/schema.ts`, so the generic
 * `apiGet`/`apiPost` from `src/api/client.ts` cover them directly — no
 * hand-rolled request plumbing needed here.
 *
 * Scope, per *Abonnement (Mollie, voor de gebruiker zelf)*: this is the
 * user's own subscription only. The Apple/Google verify/restore paths and
 * every `/subscriptions/admin/*` and `/subscriptions/webhooks/*` path belong
 * to the apps and to Beheer › Subscriptions respectively, not to this
 * feature.
 */

import { apiGet, apiPost } from '@/api/client';
import type { components } from '@/api/schema';

export type SubscriptionEntitlement = components['schemas']['SubscriptionEntitlement'];
export type SubscriptionProduct = components['schemas']['SubscriptionProduct'];
export type SubscriptionHistoryEntry = components['schemas']['SubscriptionHistoryEntry'];
export type SubscriptionWithdrawalPreview = components['schemas']['SubscriptionWithdrawalPreview'];
export type SubscriptionPlan = 'monthly' | 'yearly';

export function getEntitlement(): Promise<SubscriptionEntitlement> {
  return apiGet('/subscriptions/entitlement');
}

/** Dashboard callers never pass `?platform` — that query param is app-only. */
export function getProducts(): Promise<{ products?: SubscriptionProduct[] }> {
  return apiGet('/subscriptions/products');
}

export function getHistory(): Promise<{ purchases?: SubscriptionHistoryEntry[] }> {
  return apiGet('/subscriptions/history');
}

/** `consentImmediatePerformance` must be `true` (checked box) or the API
 * refuses with `consent_required` before ever calling Mollie. */
export function startCheckout(
  product: SubscriptionPlan,
  consentImmediatePerformance: boolean,
): Promise<{ checkout_url?: string }> {
  return apiPost('/subscriptions/mollie/start', {
    body: { product, consent_immediate_performance: consentImmediatePerformance },
  });
}

export function cancelSubscription(): Promise<{ status?: string; access_until?: string }> {
  return apiPost('/subscriptions/mollie/cancel');
}

export function switchPlan(product: SubscriptionPlan) {
  return apiPost('/subscriptions/mollie/switch-plan', { body: { product } });
}

export function getWithdrawalPreview(): Promise<SubscriptionWithdrawalPreview> {
  return apiGet('/subscriptions/mollie/withdraw');
}

export function withdraw(): Promise<{ refund_amount?: number; refund_currency?: string }> {
  return apiPost('/subscriptions/mollie/withdraw');
}
